import isEmpty from 'lodash/isEmpty'
import { ageGroupServed, getAgeGroupsFromProvider } from 'app/Bookings/utils'
import { ParsedUrlQuery } from 'querystring'
import { groupFilters } from 'app/ProviderSearch/utils'
import {
  providerProfileNumberWithCode,
  RudderstackIntegrations,
  sessionStorageKeys,
} from 'constants/common'
import { providerFilterKeyMapping } from 'dataSource/ProviderSearch'
import { FILTER_KEY_MAPPING } from 'app/ProviderSearch/constants'
import logEvent from 'utils/logEvent'
import { RudderEvent } from 'constants/rudderEvents'
import { convertHtmlToString } from 'utils/common'
import credentials from 'common/ProviderCredentials/credentials'

export const monthNames = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
}

export const providerGlobalFilters = {
  CounselingApproach: 'counseling_approach',
  Language: 'language',
  AreaOfConcern: 'area_of_concern',
}

export const getDataFromProviderFilters = (
  filters: ProviderFilter[],
  type: string
): ProviderFilter[] => {
  return filters?.filter((filter) => filter.type === type)
}

export const getInsuranceDetails = (
  providerDetails: Provider
): {
  acceptsInsurance: boolean
  providerInsurances: ProviderInsurance[]
} => {
  const practice = providerDetails?.practice
  const acceptsInsurance = !isEmpty(practice?.insurances)
  const providerInsurances = providerDetails?.practice?.insurances

  return {
    acceptsInsurance,
    providerInsurances,
  }
}

type ProviderSectionDetailsType = {
  genderPronouns: ProviderFilter[]
  populationServed: ProviderFilter[]
  acceptsInsurance: boolean
  location: string
  topSpecialities: ProviderFilter[]
  otherSpecialities: ProviderFilter[]
  availabilityTags: ProviderFilter[]
  agesServed: ProviderFilter[]
  counselingApproach?: ProviderFilter[]
  providerFeatureTags: ProviderFilter[]
  providerInsurances: ProviderInsurance[]
  LGBTQIAExperience: ProviderFilter[]
  languagesSpoken: ProviderFilter[]
}

export const getProviderSectionDetails = (
  providerDetails: Provider
): ProviderSectionDetailsType => {
  const genderPronouns = providerDetails?.filters?.filter(
    (filter) => filter.type === 'gender_pronoun'
  )

  const populationServed = providerDetails?.filters?.filter(
    (filter) => filter.type === 'counseling_type'
  )

  const practice = providerDetails?.practice
  const acceptsInsurance = !isEmpty(practice?.insurances)

  const location = practice ? `${practice.city}, ${practice.state}` : null
  const topSpecialities = providerDetails?.filters?.filter(
    (filter) => filter.type === 'area_of_concern' && filter.priority === 1
  )
  const otherSpecialities = providerDetails?.filters?.filter(
    (filter) => filter.type === 'area_of_concern' && filter.priority === 2
  )
  const availabilityTags = providerDetails?.filters?.filter(
    (filter) => filter.type === 'availability_type'
  )
  const agesServed = providerDetails?.filters?.filter(
    (filter) => filter.type === 'client_age_group'
  )
  const counselingApproach = providerDetails?.filters?.filter(
    (filter) => filter.type === 'counseling_approach'
  )
  const providerFeatureTags = providerDetails?.filters?.filter(
    (filter) => filter.type === 'tag'
  )

  const languagesSpoken = providerDetails?.filters?.filter(
    (filter) => filter.type === 'language'
  )

  const LGBTQIAExperience = providerDetails?.filters?.filter((filter) =>
    filter.value?.toLowerCase().includes('lgbt')
  )

  const providerInsurances = providerDetails?.practice?.insurances

  return {
    genderPronouns,
    populationServed,
    acceptsInsurance,
    location,
    topSpecialities,
    otherSpecialities,
    availabilityTags,
    agesServed,
    counselingApproach,
    providerFeatureTags,
    providerInsurances,
    LGBTQIAExperience,
    languagesSpoken,
  }
}

export const getProviderTitle = (providerDetails: Provider): string => {
  const titleFilter = providerDetails?.filters?.find(
    (providerFilter) => providerFilter.type === 'provider_title'
  )
  return titleFilter?.value
}

//checks if a given text is a valid url
export const isValidHttpUrl = (text: string): boolean => {
  let url: URL

  try {
    url = new URL(text)
  } catch (error) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

//removes http, https, or www from a url. Ex: https://www.youtube.com => youtube.com
export const formattedURL = (url: string): string => {
  return url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split('/')[0]
}

export const getCombinedAgeRangeFromProvider = (
  providerDetails: Provider
): string => {
  const combinedSortedAges = ageGroupServed(
    getAgeGroupsFromProvider(providerDetails)
  )
  return getCombinedAgeRange(combinedSortedAges)
}

export const getCombinedAgeRange = (ageGroups: number[][]): string => {
  //Following code is to combine the various consequitive age ranges to bigger
  //chunk of age range if possible.
  //For example, when the different age ranges are all of them right from 0-5 to 65+,
  //The function returns string '0-65+'
  //The input for this function should look like this: [[0,5], [6,10], [11,13], [14,17], [18,64], [65,299]]
  const endIndex = ageGroups?.length - 1

  //if the last two indices of combinedSortedAges are [18, 64], [65,299]] respectively
  //then combine them to [18,64]
  if (
    ageGroups?.[endIndex]?.[0] === 65 &&
    ageGroups?.[endIndex - 1]?.[1] === 64
  ) {
    ageGroups[endIndex - 1][1] = 65
    ageGroups?.pop()
  }

  const sortedAgeRange = ageGroups?.map((ageGroup) => {
    if (ageGroup?.[0] === 65) {
      return '65+'
    }

    let ageRange = ageGroup?.join('-')

    if (ageGroup?.[1] === 65) {
      ageRange += '+'
    }

    return ageRange
  })

  return sortedAgeRange?.join(', ')
}

export const getPreviousRouterUrlQuery = (): ParsedUrlQuery => {
  const previousRouter = JSON.parse(
    sessionStorage.getItem(sessionStorageKeys.PREVIOUS_ROUTER)
  )

  return previousRouter?.query || {}
}

export const getFilterValueFromId = (
  groupedFilters: GroupedProviderFilter,
  query: ParsedUrlQuery
): { [key: string]: string | string[] | number } | null => {
  const trackerInformation = {}

  Object.keys(providerFilterKeyMapping).forEach((providerFilterKey) => {
    const queryValue = query[providerFilterKey]

    if (queryValue) {
      const filterKeyMapped =
        FILTER_KEY_MAPPING[providerFilterKey.replace('[]', '')]
      const providerSection = groupedFilters[filterKeyMapped]

      if (providerSection) {
        const selectedValues = providerSection
          .filter((filter) => query[providerFilterKey].includes(filter.id))
          .map((filter) => filter.value)

        trackerInformation[filterKeyMapped] = selectedValues
      }
    }

    return null
  })

  return trackerInformation
}

/**
 * get all filters for this provider, and find the values
 * of any filter ids existing in the URL query
 */
export const queryFilters = (
  providerDetails: Provider,
  query: ParsedUrlQuery
): {
  [key: string]: string | string[] | number
} => {
  const filters = providerDetails?.filters

  if (filters) {
    const groupedFilters = groupFilters(filters)
    return getFilterValueFromId(groupedFilters, query)
  }

  return {}
}

/**
 * Structure data for tracker
 */
export const setTrackProviderInformation = (
  providerDetails: Provider
): {
  provider_name: string
  provider_id: string
  practice_state: string
  practice_city: string
} => {
  const { id, firstName, lastName, practice } = providerDetails

  return {
    provider_name: `${firstName} ${lastName}`,
    provider_id: id,
    practice_state: practice?.state,
    practice_city: practice?.city,
  }
}

/**
 * RudderStack Event that passes basic provider information
 * and any additional data you'd like to append
 * to the event of your choice
 */
export const providerRudderStackEvent = (
  eventEnum: RudderEvent,
  providerDetails: Provider,
  additionalTrackerInformation?: { [key: string]: string | string[] | number }
): void => {
  const trackProviderInformation = setTrackProviderInformation(providerDetails)

  logEvent(
    eventEnum,
    {
      ...trackProviderInformation,
      ...additionalTrackerInformation,
    },
    {
      [RudderstackIntegrations.GOOGLE_ADS]: true,
    }
  )
}

export const generateProviderProfileLdJsonSchema = (
  profileType: string,
  providerDetails: Provider,
  isProviderAvailableForIntake: boolean
): LdJsonSchema => {
  const {
    firstName,
    lastName,
    practice,
    filters,
    profileImagePath = 'https://thriveworks.com/images/default_provider.svg',
    shortIntro,
    gender,
    academics,
    about,
    faq,
  } = providerDetails ?? {}

  const { address, city, state, zipcode, latitude, longitude } = practice ?? {}

  const formattedZipcode = zipcode?.slice(0, 5)

  const { degrees } = academics || {}
  const { payrates } = about

  const {
    topSpecialities,
    otherSpecialities,
    languagesSpoken,
  } = getProviderSectionDetails(providerDetails)

  let providerLdJsonSchemaData
  let paymentAccepted = ['']

  const providerProfilePageUrl =
    window.location.origin + window.location.pathname

  const providerProfileEntityUrl = `${providerProfilePageUrl}#profile`

  // If any of the degree has showOnsummary and something from name or university then return true
  const hasDegrees = degrees?.some(
    (degree) => degree?.showOnSummary && (degree?.name || degree?.university)
  )

  const degreeUniversity = hasDegrees
    ? degrees?.filter((degree) => degree.showOnSummary)[0]?.university
    : ''

  const supportsSelfPay = !isEmpty(payrates)
  const supportsInsurance = providerDetails?.practice?.insurances?.length > 0

  if (supportsSelfPay) {
    paymentAccepted = ['Credit Card', 'Self-pay']
  }

  if (supportsInsurance) {
    paymentAccepted = ['Insurance']
  }

  if (supportsSelfPay && supportsInsurance) {
    paymentAccepted = ['Insurance', 'Credit Card', 'Self-pay']
  }

  const titleAbbreviation = getProviderTitle(providerDetails)
  const title = credentials[titleAbbreviation]?.definition ?? titleAbbreviation

  const approaches = getDataFromProviderFilters(
    filters,
    providerGlobalFilters.CounselingApproach
  )

  const availableService = approaches?.map((approach) => {
    return {
      '@type': 'MedicalTherapy',
      name: approach.value,
    }
  })

  const allSpecialities = [...topSpecialities, ...otherSpecialities].map(
    (speciality) => speciality.value
  )

  if (profileType === 'therapist') {
    providerLdJsonSchemaData = {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      '@id': providerProfileEntityUrl,
      url: providerProfilePageUrl,
      mainEntity: {
        '@type': 'Person',
        '@id': providerProfilePageUrl,
        name: `${firstName} ${lastName}`,
        description: convertHtmlToString(shortIntro),
        image: {
          '@type': 'ImageObject',
          '@id': profileImagePath,
          url: profileImagePath,
          caption: `${firstName} ${lastName}`,
        },
        gender: gender,
        jobTitle: title,
        affiliation: {
          '@type': 'Organization',
          '@id': 'https://thriveworks.com',
          name: 'Thriveworks Counseling',
          url: 'https://thriveworks.com',
        },
        worksFor: {
          '@type': 'Organization',
          '@id': 'https://thriveworks.com#organization',
          name: 'Thriveworks Counseling',
          url: 'https://thriveworks.com',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: address.split(',')[0],
          addressLocality: city,
          addressRegion: state,
          postalCode: formattedZipcode,
          addressCountry: 'US',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: providerProfileNumberWithCode,
          contactType: 'Work',
        },
        knowsAbout: allSpecialities,
        knowsLanguage: languagesSpoken.map((language) => language.value),
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': providerProfilePageUrl,
        isPartOf: {
          '@type': 'WebSite',
          '@id': 'https://thriveworks.com/',
          name: 'Thriveworks Counseling',
        },
      },
    }
  } else {
    providerLdJsonSchemaData = {
      '@context': 'https://schema.org',
      '@type': 'Physician',
      '@id': providerProfileEntityUrl,
      url: providerProfilePageUrl,
      name: `${firstName} ${lastName}`,
      description: convertHtmlToString(shortIntro),
      image: {
        '@type': 'ImageObject',
        '@id': profileImagePath,
        url: profileImagePath,
        caption: `${firstName} ${lastName}`,
      },
      gender: gender,
      jobTitle: title,
      worksFor: {
        '@type': 'MedicalOrganization',
        '@id': 'https://thriveworks.com',
        name: 'Thriveworks Counseling',
        url: 'https://thriveworks.com',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: address.split(',')[0],
        addressLocality: city,
        addressRegion: state,
        postalCode: formattedZipcode,
        addressCountry: 'US',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: providerProfileNumberWithCode,
        contactType: 'Work',
      },
      alumniOf: {
        '@type': 'EducationalOrganization',
        name: degreeUniversity,
      },
      medicalSpecialty: 'Psychiatry',
      knowsAbout: allSpecialities,
      knowsLanguage: languagesSpoken.map((language) => language.value),
      isAcceptingNewPatients: isProviderAvailableForIntake,
      paymentAccepted: paymentAccepted,
      geo: {
        '@type': 'GeoCoordinates',
        latitude: `${latitude}`,
        longitude: `${longitude}`,
      },
      hasCertification: titleAbbreviation ? titleAbbreviation.split(' ') : [''],
      availableService: availableService,
      brand: {
        '@type': 'Brand',
        name: 'Thriveworks Counseling',
        url: 'https://thriveworks.com',
        logo: 'https://thriveworks.com/images/Sunbeam.svg',
      },
      mainEntityOfPage: {
        '@type': 'ProfilePage',
        '@id': providerProfilePageUrl,
        isPartOf: {
          '@type': 'WebSite',
          '@id': 'https://thriveworks.com/',
          name: 'Thriveworks Counseling',
        },
      },
    }
  }

  if (!!faq?.length) {
    const formattedFaqData = faq.map(({ question, answer }) => {
      const questionText = convertHtmlToString(question)

      const answerText = convertHtmlToString(answer)

      return {
        '@type': 'Question',
        name: questionText,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answerText,
        },
      }
    })

    const ldJsonSchemaDataForFAQ = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: formattedFaqData,
    }

    return [providerLdJsonSchemaData, ldJsonSchemaDataForFAQ]
  }

  return providerLdJsonSchemaData
}

export function filterAndSortSimilarProviders(
  providerDetails: Provider,
  otherProvidersFromPractice: ProviderMetadataForPractice[]
): ProviderMetadataForPractice[] {
  const {
    otherSpecialities,
    topSpecialities,
    populationServed,
    providerInsurances,
  } = getProviderSectionDetails(providerDetails)

  const counselingTypeIds = populationServed?.map(
    (population) => population?.id
  )
  const specialitiesIds = [...topSpecialities, ...otherSpecialities].map(
    (speciality) => speciality?.id
  )
  const insurancesIds = providerInsurances?.map(
    (insurance) =>
      insurance?.insuranceProviderId || insurance?.insuranceProvider?.id
  )

  // Filter out the providers that have at least one match each for speciality, counseling type and insurance
  const filteredProviders = otherProvidersFromPractice.filter((provider) => {
    if (
      provider.id !== providerDetails.id &&
      provider.isAvailable &&
      provider?.areaOfConcernIds?.some((id) => specialitiesIds?.includes(id)) &&
      provider?.counselingTypeIds?.some((id) =>
        counselingTypeIds?.includes(id)
      ) &&
      provider?.insuranceProviderIds?.some((id) => insurancesIds?.includes(id))
    ) {
      return true
    }

    return false
  })

  if (filteredProviders.length === 0) {
    return []
  }

  const matchedProviders = filteredProviders.map((provider) => {
    let matchScore = 0

    const {
      areaOfConcernIds,
      counselingTypeIds,
      insuranceProviderIds,
    } = provider

    const matchedAreaOfConcerns = areaOfConcernIds?.filter((id) =>
      specialitiesIds?.includes(id)
    )
    const matchedCounselingTypes = counselingTypeIds?.filter((id) =>
      counselingTypeIds?.includes(id)
    )
    const matchedInsurances = insuranceProviderIds?.filter((id) =>
      insurancesIds?.includes(id)
    )

    matchScore +=
      matchedAreaOfConcerns.length +
      matchedCounselingTypes.length +
      matchedInsurances.length

    return { ...provider, matchScore }
  })

  const similarProviders = matchedProviders.sort(
    (a, b) => b?.matchScore - a?.matchScore
  )

  // show max 6 similar providers
  return similarProviders.slice(0, 6)
}
